// Practice minutes count the time someone is practising out loud: while the
// French audio plays, while the recorder records, and while their own
// recording plays back, plus up to GRACE_MS after each of those. The grace
// covers the natural rhythm between them (waiting for the score, a quick look
// at the result, tapping on to the next phrase), so a steady session counts in
// full, while stopping, reading for ages, idling or clicking around the app
// does not (Leanne, 3 Nov 2026: the strict version credited an 18 minute
// session with 5). Applies from 3 November 2026; earlier days keep the minutes
// they were given.
//
// speakFrench (lib/tts.js) and AudioRecorder (lib/speech.js) call markActive();
// own-voice playback goes through trackPlayback(). Manual, Automatic, sound
// lessons and drills are all timed by the same rule.
import { practiceApi } from './api';

const FLUSH_EVERY_MS = 30000;
const GRACE_MS = 20000;

let open = 0;          // listening and speaking can overlap; count the union once
let openedAt = 0;
let idleSince = null;  // when the last listening or speaking ended
let bankedMs = 0;
let timer = null;

// Count the quiet spell since the last activity, up to the grace. `closing`
// ends it now (page hidden); otherwise a spell still inside the grace is left
// open, because the next phrase may yet start within it.
const settleIdle = (closing) => {
    if (idleSince == null) return;
    const gap = Date.now() - idleSince;
    if (closing || gap >= GRACE_MS) {
        bankedMs += Math.min(gap, GRACE_MS);
        idleSince = null;
    }
};

const flush = (closing = false) => {
    if (open === 0) settleIdle(closing);
    const secs = Math.floor(bankedMs / 1000);
    if (secs <= 0) return;
    bankedMs -= secs * 1000;
    practiceApi.recordSessionEnd(secs, 'active').catch(() => { /* logged out, offline: dropped */ });
};

// Start counting. Returns a function that stops it; safe to call twice.
export const markActive = () => {
    if (open++ === 0) {
        if (idleSince != null) {
            bankedMs += Math.min(Date.now() - idleSince, GRACE_MS);
            idleSince = null;
        }
        openedAt = Date.now();
    }
    if (!timer) timer = setInterval(() => flush(false), FLUSH_EVERY_MS);
    let ended = false;
    return () => {
        if (ended) return;
        ended = true;
        if (--open === 0) {
            bankedMs += Date.now() - openedAt;
            idleSince = Date.now();
        }
    };
};

// Count an <audio> element while it plays the learner's own recording. The
// listeners come off at the first pause, end or source change, because the
// same element can be reused later for the French audio, which is timed
// separately. Call it before play().
export const trackPlayback = (audio) => {
    if (!audio) return;
    let end = null;
    const onPlay = () => { if (!end) end = markActive(); };
    const detach = () => {
        end?.();
        end = null;
        audio.removeEventListener('play', onPlay);
        ['pause', 'ended', 'error', 'emptied'].forEach(e => audio.removeEventListener(e, detach));
    };
    audio.addEventListener('play', onPlay);
    ['pause', 'ended', 'error', 'emptied'].forEach(e => audio.addEventListener(e, detach));
};

// Hand over what has been counted when the page is hidden or closed, so a
// session ended by locking the phone is not lost. Leaving the app also ends
// the grace there and then.
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
}
